import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import type { ActivityItem } from './ActivityFeed';

interface ActivityDetailDrawerProps {
  item: ActivityItem | null;
  open: boolean;
  onClose: () => void;
}

const AVATAR_COLORS = [
  '#C84B1F',
  '#4A6B3A',
  '#2B3D54',
  '#8A5A3C',
  '#6B4E7D',
  '#B68A2E'
];

function userColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++)
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'create':
      return 'Action list created';
    case 'share':
      return 'List shared';
    case 'note':
      return 'Private note added';
    case 'status':
      return 'Status changed';
    case 'visit':
      return 'Visit logged';
    case 'edit':
      return 'Action list edited';
    case 'email':
      return 'Email sent';
    default:
      return 'Activity';
  }
}

export const ActivityDetailDrawer: React.FC<ActivityDetailDrawerProps> = ({
  item,
  open,
  onClose
}) => {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  return (
    <>
      <Overlay $open={open} onClick={onClose} />
      <Drawer $open={open} role="dialog" aria-modal="false" aria-hidden={!open}>
        <Head>
          <Eyebrow>
            Activity detail{' '}
            {item && (
              <KindPill $kind={item.kind}>{item.kind.toUpperCase()}</KindPill>
            )}
          </Eyebrow>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseBtn>
        </Head>
        {item && (
          <Body>
            <Title>{kindLabel(item.kind)}</Title>
            <Summary>
              {item.kind === 'create' && <>&ldquo;{item.target}&rdquo;</>}
              {item.kind === 'share' && <>&ldquo;{item.target}&rdquo;</>}
              {item.kind === 'note' && (
                <>
                  On <strong>{item.target}</strong>
                </>
              )}
              {item.kind === 'status' && (
                <>
                  {item.target}{' '}
                  {item.detail && <SummaryPill>{item.detail}</SummaryPill>}
                </>
              )}
              {item.kind === 'visit' && (
                <>
                  Visit at <strong>{item.target}</strong>
                </>
              )}
              {item.kind === 'edit' && <>&ldquo;{item.target}&rdquo;</>}
              {item.kind === 'email' && (
                <>
                  Sent to <strong>{item.recipientCount || 1}</strong> POS
                </>
              )}
            </Summary>

            <MetaBlock>
              <MetaAvatar $color={userColor(item.user)}>
                {item.initials}
              </MetaAvatar>
              <MetaText>
                By <strong>{item.user}</strong>
                <MetaSep>·</MetaSep>
                {item.when}
              </MetaText>
            </MetaBlock>

            {/* Kind-specific sections */}
            {item.kind === 'create' && (
              <>
                <Section>
                  <KvRow>
                    <KvKey>Privacy</KvKey>
                    <KvVal>
                      {item.privacy === 'shared' ? 'Shared' : 'Private'}
                    </KvVal>
                  </KvRow>
                  {item.sentTo && (
                    <KvRow>
                      <KvKey>Shared with</KvKey>
                      <KvVal>{item.sentTo}</KvVal>
                    </KvRow>
                  )}
                  {item.posCount && (
                    <KvRow>
                      <KvKey>POS in list</KvKey>
                      <KvVal>{item.posCount}</KvVal>
                    </KvRow>
                  )}
                </Section>
              </>
            )}

            {item.kind === 'share' && item.with_ && (
              <Section>
                <KvRow>
                  <KvKey>Shared with</KvKey>
                  <KvVal>{item.with_}</KvVal>
                </KvRow>
              </Section>
            )}

            {item.kind === 'note' && (
              <Section>
                <SectionLabel>Point of sale</SectionLabel>
                <PosLink>{item.target}</PosLink>
                <KvRow>
                  <KvKey>Privacy</KvKey>
                  <KvVal>Private — visible only to {item.user}</KvVal>
                </KvRow>
              </Section>
            )}

            {item.kind === 'status' && (
              <Section>
                <SectionLabel>Point of sale</SectionLabel>
                <PosLink>{item.target}</PosLink>
                {item.detail && (
                  <KvRow>
                    <KvKey>Transition</KvKey>
                    <KvVal>{item.detail}</KvVal>
                  </KvRow>
                )}
              </Section>
            )}

            {item.kind === 'visit' && (
              <Section>
                <SectionLabel>Point of sale</SectionLabel>
                <PosLink>{item.target}</PosLink>
              </Section>
            )}

            {item.kind === 'email' && (
              <Section>
                <SectionLabel>Subject</SectionLabel>
                <SubjectCard>{item.target || '(no subject)'}</SubjectCard>
              </Section>
            )}

            {item.kind === 'edit' && (
              <Section>
                <SectionLabel>Action list</SectionLabel>
                <PosLink>{item.target}</PosLink>
              </Section>
            )}
          </Body>
        )}
      </Drawer>
    </>
  );
};

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(43, 61, 84, 0.32);
  z-index: 5990;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.22s ease;

  ${(p) =>
    p.$open &&
    css`
      opacity: 1;
      pointer-events: auto;
    `}
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: min(560px, 100vw);
  height: 100vh;
  background: var(--white, #fff);
  box-shadow: -8px 0 32px rgba(43, 61, 84, 0.18);
  transform: translateX(100%);
  transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 6000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${(p) =>
    p.$open &&
    css`
      transform: translateX(0);
    `}
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--line-soft, var(--line));
  background: var(--paper);
`;

const Eyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-muted);
`;

const KindPill = styled.span<{ $kind: string }>`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin-left: 8px;

  ${(p) =>
    p.$kind === 'email' &&
    css`
      background: rgba(200, 75, 31, 0.12);
      color: var(--terracotta);
    `}
  ${(p) =>
    (p.$kind === 'create' || p.$kind === 'share' || p.$kind === 'visit') &&
    css`
      background: rgba(74, 107, 58, 0.12);
      color: #4a6b3a;
    `}
  ${(p) =>
    (p.$kind === 'note' || p.$kind === 'edit') &&
    css`
      background: rgba(43, 61, 84, 0.08);
      color: #2b3d54;
    `}
  ${(p) =>
    p.$kind === 'status' &&
    css`
      background: rgba(182, 138, 46, 0.14);
      color: #8a6422;
    `}
`;

const CloseBtn = styled.button`
  background: var(--white, #fff);
  border: 1px solid var(--line);
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  cursor: pointer;
  color: var(--ink-muted);
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;

  &:hover {
    background: var(--paper);
    color: var(--ink);
    border-color: var(--ink-muted);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Body = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const Title = styled.h2`
  font-family: 'Crimson Pro', 'DM Sans', serif;
  font-size: 26px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.01em;
  margin: 0 0 6px;
  line-height: 1.2;
`;

const Summary = styled.p`
  font-size: 13.5px;
  color: var(--ink-muted);
  line-height: 1.5;
  margin: 0 0 22px;

  strong {
    font-weight: 600;
    color: var(--ink);
  }
`;

const SummaryPill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(200, 75, 31, 0.1);
  color: var(--terracotta);
  margin-left: 4px;
`;

const MetaBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--paper);
  border-radius: 8px;
  margin-bottom: 24px;
`;

const MetaAvatar = styled.div<{ $color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: var(--white, #fff);
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${(p) => p.$color};
`;

const MetaText = styled.div`
  font-size: 12.5px;
  color: var(--ink);
  line-height: 1.5;

  strong {
    font-weight: 600;
  }
`;

const MetaSep = styled.span`
  color: var(--ink-faint);
  margin: 0 4px;
`;

const Section = styled.div`
  margin-bottom: 22px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-muted);
  margin-bottom: 10px;
`;

const KvRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 10px 14px;
  background: var(--paper);
  border-radius: 7px;
  margin-bottom: 6px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const KvKey = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-muted);
`;

const KvVal = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  text-align: right;
`;

const PosLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--white, #fff);
  border: 1px solid var(--line);
  border-radius: 7px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s,
    background 0.15s;
  margin-bottom: 12px;

  &:hover {
    border-color: var(--terracotta);
    color: var(--terracotta);
    background: rgba(200, 75, 31, 0.04);
  }
`;

const SubjectCard = styled.div`
  background: var(--paper);
  border-left: 3px solid var(--terracotta);
  padding: 14px 16px;
  border-radius: 6px;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.4;
`;
